import { useRef } from 'react';
import { last } from 'lodash/fp';

// Services
import logger from '../../../../../../logger/logger';
import http from '../../../../../../utils/services/http';
import fetchEventSource from '../../../../../../utils/services/http/fetchEventSource';

// Utils
import { isGlobalDebugEnabled } from '../../../../../../debug/debugUtil';
import { getElementType } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { formatIdeaObjectForRender } from '../../../../../../../common/ai/aiResponseFormattingUtils';

// Analytics
import { sendAmplitudeEvent } from '../../../../../../analytics/amplitudeService';
import { EVENT_TYPE_NAMES } from '../../../../../../../common/analytics/amplitudeEventTypesUtil';

// Types
import { UseAiToolState } from './aiToolStateTypes';
import { AiConversationMessage } from '../../../../../../../common/ai/aiConversationTypes';

// Constants
import { AIConversationResultType } from './aiToolStateConstants';
import { TIMES } from '../../../../../../../common/utils/timeUtil';

export interface StreamRequestHandlers {
    handlePromptSubmit: (prompts: AiConversationMessage[]) => Promise<void>;
    handleCancelStream: () => void;
}

const useSubmitAiStreamRequestHandlers = (props: UseAiToolState): StreamRequestHandlers => {
    const {
        currentConversationId,
        setConversationTitle,
        setConversationStreamingResults,
        setConversationError,
        currentConversation,
        appendPrompt,
        appendResult,
        appendDebugInfo,
        removeResult,
    } = props;

    const abortStreamingControllerRef = useRef<AbortController>();
    const abortStreamingTimerRef = useRef<NodeJS.Timeout>();

    const fetchConversationTitle = async (conversationId: string, prompts: AiConversationMessage[]): Promise<void> => {
        const currentTitle = currentConversation?.title || '';
        if (currentTitle !== '') return;

        const response = await http({
            url: `ai/conversations/title`,
            method: 'POST',
            data: {
                conversationId,
                prompts,
            },
        });

        const { conversation } = response.data;

        setConversationTitle(conversation._id, conversation.title);
    };

    const removePromptIfNoResults = (conversationId: string): void => {
        const conversation = currentConversation;
        if (!conversation) return;

        const lastResult = last(conversation.results);
        if (!lastResult) return;

        const lastResultIsPrompt = lastResult.elementType === AIConversationResultType.PROMPT_HISTORY;
        if (!lastResultIsPrompt || !lastResult.uuid) return;

        removeResult(conversationId, lastResult.uuid);
    };

    const endStreaming = (): void => {
        setConversationStreamingResults(currentConversationId, false);
        removePromptIfNoResults(currentConversationId);
        abortStreamingControllerRef.current?.abort();
        clearTimeout(abortStreamingTimerRef.current);
    };

    const fetchPromptData = async (prompts: AiConversationMessage[]): Promise<void> => {
        setConversationError(currentConversationId, null);
        setConversationStreamingResults(currentConversationId, true);

        const userMessage = last(prompts.filter((message) => message.visibility.user));
        appendPrompt(currentConversationId, userMessage?.content || '');

        const abortController = new AbortController();
        abortStreamingControllerRef.current = abortController;

        // if the stream is not closed within 30 seconds, abort it
        abortStreamingTimerRef.current = setTimeout(() => {
            endStreaming();
        }, TIMES.SECOND * 30);

        await fetchEventSource({
            url: 'ai/conversations/stream',
            openWhenHidden: true,
            signal: abortController.signal,
            data: {
                conversationId: currentConversationId,
                prompts,
            },
            onopen: async (response) => {
                if (response.ok) return; // don't do anything if the stream is open & ok
                // otherwise, proceed to error handling

                const responseJson = await response.json();
                const streamErrorMessage = responseJson?.error.message || 'Unknown error';

                setConversationError(currentConversationId, streamErrorMessage);
                endStreaming();
            },
            onmessage: (event) => {
                try {
                    const { conversationId, idea, debug, error } = JSON.parse(event.data);

                    if (error) {
                        console.error('error', error);
                        setConversationError(conversationId, error.message);
                    }

                    if (idea) {
                        const resultElement = formatIdeaObjectForRender(idea);
                        if (resultElement) {
                            sendAmplitudeEvent({
                                eventType: EVENT_TYPE_NAMES.AI_RESULT_GENERATED,
                                eventProperties: {
                                    elementType: getElementType(resultElement),
                                },
                            });

                            appendResult(conversationId, resultElement);
                            return;
                        }
                    }

                    if (debug && isGlobalDebugEnabled()) {
                        console.info('debug', debug);
                        appendDebugInfo(conversationId, debug);
                    }
                } catch (error) {
                    logger.error('AI: Could not parse stream result', error);
                }
            },
            onerror: (event) => {
                logger.error('AI: Fetching stream result', event);

                setConversationError(currentConversationId, 'Something went wrong. Please try again.');

                endStreaming();
            },
            onclose: () => {
                endStreaming();
            },
        });
    };

    const handleCancelStream = (): void => {
        if (!abortStreamingControllerRef.current) return;

        endStreaming();
    };

    const handlePromptSubmit = async (prompts: AiConversationMessage[]): Promise<void> => {
        await Promise.allSettled([fetchPromptData(prompts), fetchConversationTitle(currentConversationId, prompts)]);
    };

    return {
        handlePromptSubmit,
        handleCancelStream,
    };
};

export default useSubmitAiStreamRequestHandlers;
